/**
 * What the customer tracking page says about when their technician gets there.
 *
 * The page has two very different things it can know — a live drive time,
 * recomputed from the tech's current position, and the arrival the visit was
 * scheduled for — and it used to word them identically. That is how a customer
 * ended up reading "Arrival 2:15 PM" at four o'clock, and a "22 min" countdown
 * for a technician who had not left the shop. The rules are pulled out here so
 * the wording is decided in one tested place rather than inside a render.
 */

export interface ArrivalStateInput {
  /** The tech is at the service address — nothing left to count down. */
  atSite: boolean;
  /**
   * Minutes of driving still to go, and only from something live: a route
   * computed off the tech's current position, or a live-sourced backend ETA.
   * A planned drive time is not a countdown and must arrive here as null.
   */
  driveMinutes: number | null;
  /** The arrival instant we hold, live or scheduled; null when we hold none. */
  arrivalAt: string | null;
  /** Whether the tech's position is on the page at all. */
  hasTechLocation: boolean;
  now: number;
}

export type ArrivalState =
  | { kind: 'at_site' }
  /** A live ETA, still ahead of us — name the time. */
  | { kind: 'arriving_at'; arrivalAt: string }
  /** A live ETA the tech has already run past — they are close, don't quote it. */
  | { kind: 'arriving_shortly' }
  /** No live ETA, but a scheduled arrival still ahead — name it as a plan. */
  | { kind: 'scheduled_for'; arrivalAt: string }
  /** Position but no usable arrival yet. */
  | { kind: 'calculating' }
  /** Nothing live at all — the tech hasn't set off. */
  | { kind: 'not_departed' };

export function arrivalState(input: ArrivalStateInput): ArrivalState {
  if (input.atSite) return { kind: 'at_site' };

  const arrivalAt = input.arrivalAt;
  const passed = arrivalAt != null && new Date(arrivalAt).getTime() < input.now;

  if (input.driveMinutes != null && arrivalAt) {
    // An arrival that has come and gone is no longer an estimate. The backend
    // only serves a LIVE ETA while the tech's pings are fresh, so we know they
    // are still on their way — say that instead of quoting a stale clock time.
    return passed ? { kind: 'arriving_shortly' } : { kind: 'arriving_at', arrivalAt };
  }
  if (arrivalAt && !passed) return { kind: 'scheduled_for', arrivalAt };
  return input.hasTechLocation ? { kind: 'calculating' } : { kind: 'not_departed' };
}

/**
 * The state as one line of customer-facing copy. `clock` renders an instant in
 * the service location's timezone, which only the page knows how to do.
 */
export function arrivalLine(state: ArrivalState, clock: (iso: string) => string): string {
  switch (state.kind) {
    case 'at_site':
      return 'Your technician is at your location';
    case 'arriving_at':
      return `Arrival ${clock(state.arrivalAt)}`;
    case 'arriving_shortly':
      return 'Arriving shortly';
    case 'scheduled_for':
      return `Scheduled arrival ${clock(state.arrivalAt)}`;
    case 'calculating':
      return 'Calculating arrival…';
    case 'not_departed':
      return 'Live location starts once your technician departs';
  }
}
